import { PureComponent } from 'react';
import {
  Badge,
  ButtonGroup,
  Card,
  ListGroup,
  ToggleButton
} from 'react-bootstrap';
import { Select, Option } from 'idea-react';

import * as styles from './index.module.less';
import {
  DepthItem,
  DepthLevel,
  DepthType
} from '../../../model/canister/Depth';
import { observer } from 'mobx-react';
import { SpotOrderType } from '../../../model/canister/Fusion';
import { formatNumber } from '../../../model/utils/helper';
import classnames from 'classnames';
import { LatestPrice, PriceTrend } from '../../../model/market';
import BigNumber from 'bignumber.js';

interface Props {
  askOrders?: Array<DepthItem>;
  bidOrders?: Array<DepthItem>;
  type?: DepthType;
  levels?: Array<number>;
  level?: DepthLevel;
  count?: number;
  latestPrice?: LatestPrice;
  orderPricePrecision?: number;
  depthVolumePrecision?: number;
  baseTokenPrecision?: number;
  baseTokenSymbol?: string;
  quoteTokenSymbol?: string;
  onChangeDepthType?: (depthType: DepthType) => void;
  onChangeDepthLevel?: (level: DepthLevel) => void;
  onAskOrderClick?: (order: DepthItem) => void;
  onBidOrderClick?: (order: DepthItem) => void;
}

@observer
export class Depth extends PureComponent<Props> {
  radios = [DepthType.All, DepthType.Bid, DepthType.Ask].map(d =>
    DepthType[d].toString()
  );

  renderButtonGroup() {
    const { type = DepthType.All, onChangeDepthType = () => {} } = this.props;
    return (
      <ButtonGroup size="sm" className={styles['toggle-btn-group']}>
        {this.radios.map((radio, idx) => (
          <ToggleButton
            className={`${styles['toggle-btn']} ${styles[radio.toLowerCase()]}`}
            key={idx}
            id={`radio-${idx}`}
            type="radio"
            name="radio"
            value={radio}
            checked={DepthType[type].toString() === radio}
            onChange={() => onChangeDepthType(DepthType[radio])}
          />
        ))}
      </ButtonGroup>
    );
  }

  renderSelect() {
    const {
      level = DepthLevel.Two,
      levels = [],
      onChangeDepthLevel = () => {}
    } = this.props;

    return (
      <div className={styles['depth-select']}>
        <Select
          variant="transparent"
          menuVariant="dark"
          className="text-secondary fs-12"
          value={level.toString()}
          onChange={v => onChangeDepthLevel(DepthLevel[v])}
        >
          {levels.map((item, idx) => (
            <Option className="fs-12" key={idx} value={(idx + 1).toString()}>
              {item}
            </Option>
          ))}
        </Select>
      </div>
    );
  }

  renderTitle() {
    const { baseTokenSymbol = '', quoteTokenSymbol = '' } = this.props;
    return (
      <Card.Title
        className={`d-flex justify-content-between align-items-center mb-0 ${styles['trade-list-header']}`}
      >
        <span className={styles['title-item']}>
          Price <Badge className={styles.badge}>{quoteTokenSymbol}</Badge>
        </span>
        <span className={styles['title-item']}>
          Amount <Badge className={styles.badge}>{baseTokenSymbol}</Badge>
        </span>
        <span className={styles['title-item']}>
          Sum <Badge className={styles.badge}>{baseTokenSymbol}</Badge>
        </span>
      </Card.Title>
    );
  }

  renderPrice() {
    const {
      type = DepthType.All,
      latestPrice = { price: 0, trend: PriceTrend.Equal },
      orderPricePrecision = 2
    } = this.props;
    return (
      <div className={styles['price-wrap']}>
        <strong
          className={`${styles.price} ${
            latestPrice.trend === PriceTrend.Up
              ? styles.green
              : latestPrice.trend === PriceTrend.Down
              ? styles.red
              : styles.white
          }`}
        >
          {formatNumber(latestPrice.price, orderPricePrecision)}
        </strong>
        <span> ≈ {formatNumber(latestPrice.price, orderPricePrecision)}</span>
      </div>
    );
  }

  renderOrderList(orders: DepthItem[], type: SpotOrderType) {
    const {
      onAskOrderClick = () => {},
      onBidOrderClick = () => {},
      levels = [],
      level = 1,
      baseTokenPrecision = 0
    } = this.props;
    const miniPrice = levels[level - 1];
    const depthPriceDecimals = new BigNumber(miniPrice).dp();
    let volumeSum = new BigNumber(0);
    let maxVolume = new BigNumber(0);

    orders.forEach(order => {
      maxVolume = BigNumber.max(maxVolume, order.volume);
    });

    let renderOrders: Array<{
      price: BigNumber;
      volume: BigNumber;
      sum: BigNumber;
      percent: BigNumber;
    }> = orders.map(order => {
      const { price, volume } = order;
      volumeSum = volumeSum.plus(volume);
      return {
        price: price,
        volume: volume,
        sum: volumeSum,
        percent: volume.div(maxVolume).times(100)
      };
    });

    if (type === SpotOrderType.Ask) renderOrders = renderOrders.reverse();

    return renderOrders.length > 0 ? (
      <ListGroup
        className={
          type === SpotOrderType.Ask
            ? styles['trade-list-sell']
            : styles['trade-list-buy']
        }
      >
        {renderOrders.map(({ price, volume, sum, percent }, idx) => (
          <ListGroup.Item
            key={idx}
            className={`bg-dark ${styles['trade-list-item']}`}
            style={{
              backgroundSize: `${percent.dp(0).toString(10)}% 100%`
            }}
            onClick={() => {
              type === SpotOrderType.Ask
                ? onAskOrderClick(renderOrders[idx])
                : onBidOrderClick(renderOrders[idx]);
            }}
          >
            <span className={styles['trade-list-price']}>
              {formatNumber(price, depthPriceDecimals)}
            </span>
            <span className={styles['trade-list-volume']}>
              {formatNumber(volume, baseTokenPrecision)}
            </span>
            <span className={styles['trade-list-sum']}>
              {formatNumber(sum, baseTokenPrecision)}
            </span>
          </ListGroup.Item>
        ))}
      </ListGroup>
    ) : (
      <div className={styles['no-data']}>no data</div>
    );
  }

  render() {
    const { askOrders = [], bidOrders = [], type = DepthType.All } = this.props;
    return (
      <Card className={`bg-dark ${styles['depth-wrap']}`}>
        <Card.Header className={styles['depth-header']}>
          {this.renderButtonGroup()}
          {this.renderSelect()}
        </Card.Header>

        {this.renderTitle()}

        <Card.Body
          className={classnames(styles['depth-body'], {
            [styles['depth-all']]: type === DepthType.All
          })}
        >
          {type === DepthType.All || type === DepthType.Ask
            ? this.renderOrderList(askOrders, SpotOrderType.Ask)
            : null}

          {this.renderPrice()}

          {type === DepthType.All || type === DepthType.Bid
            ? this.renderOrderList(bidOrders, SpotOrderType.Bid)
            : null}
        </Card.Body>
      </Card>
    );
  }
}
